import { createAsyncThunk } from '@reduxjs/toolkit';
import { apiServiceInstanceDashboard } from '../apiUtils';
import {
  CountsData,
  EBData,
  TurfData,
  ProbeAnalysisData,
  ProbeSummaryData,
  ProbeCompareData,
  ProbeItemThemesData,
} from '../Reducers/maxdiffAnalysisReducer';

// All MaxDiff analysis thunks live here. New sub-features (choice
// probability, HB, TURF, ...) get their own thunks in this file rather
// than a new one — keeps the import surface small and matches how
// aiAnalysis.ts groups the entire qual analysis surface.

export const fetchMaxDiffCounts = createAsyncThunk<
  CountsData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    // Server-side filter — pass a JSON-serialisable list of user_ids.
    // null / undefined = no filter, aggregate every respondent.
    filterUserIds?: number[] | null;
  }
>('maxdiffAnalysis/fetchCounts', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/counts/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      // Backend uses ast.literal_eval on filterUserIds, so send a stringified
      // Python-literal-compatible list ("[1, 2, 3]"). JSON.stringify produces
      // the same syntax for lists of numbers.
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
    },
  );
  return res.data?.data as CountsData;
});

export const fetchMaxDiffEmpiricalBayes = createAsyncThunk<
  EBData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    filterUserIds?: number[] | null;
    // Advanced overrides — leave undefined to use backend defaults
    // (k=1.0, iters=500).
    shrinkageK?: number;
    bootstrapIters?: number;
  }
>('maxdiffAnalysis/fetchEmpiricalBayes', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/empirical_bayes/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
      shrinkageK: payload.shrinkageK,
      bootstrapIters: payload.bootstrapIters,
    },
  );
  return res.data?.data as EBData;
});

export const fetchMaxDiffProbeAnalysis = createAsyncThunk<
  ProbeAnalysisData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    filterUserIds?: number[] | null;
  }
>('maxdiffAnalysis/fetchProbeAnalysis', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_analysis/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
    },
  );
  return res.data?.data as ProbeAnalysisData;
});

export const fetchMaxDiffProbeSummary = createAsyncThunk<
  { key: string; data: ProbeSummaryData },
  {
    batchId: number | string;
    questionId: number | string;
    itemId: number;
    slot: 'best' | 'worst';
    languageId?: string;
    filterUserIds?: number[] | null;
    // Bypasses the server-side persistent cache and re-runs the LLM.
    // Wired to the "Regenerate" button in ProbeSummary.
    forceRefresh?: boolean;
  }
>('maxdiffAnalysis/fetchProbeSummary', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_summary/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      itemId: payload.itemId,
      slot: payload.slot,
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
      forceRefresh: payload.forceRefresh ? true : undefined,
    },
  );
  return {
    // Cache key: item × slot uniquely identifies a summary. Lets the
    // reducer store many summaries in parallel and the UI only re-fetch
    // when the analyst opens an item+slot for the first time.
    key: `${payload.itemId}:${payload.slot}`,
    data: res.data?.data as ProbeSummaryData,
  };
});

export const fetchMaxDiffProbeCompare = createAsyncThunk<
  { key: string; data: ProbeCompareData },
  {
    batchId: number | string;
    questionId: number | string;
    // Item ids in the caller's selection order — the reducer sorts them
    // before hashing for the cache key so re-selecting the same set in
    // a different order hits the cache.
    itemIds: number[];
    // Precomputed cache key (`${sortedIds.join(',')}:${filterHash}`).
    // Passed in so the pending/rejected reducers can use it too — the
    // thunk fulfilment payload doesn't get to see meta.arg after the
    // fulfilled action fires.
    cacheKey: string;
    languageId?: string;
    filterUserIds?: number[] | null;
    // Bypasses the server-side persistent cache. Wired to the compare
    // view's "Regenerate" button.
    forceRefresh?: boolean;
  }
>('maxdiffAnalysis/fetchProbeCompare', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_compare/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      // Backend uses ast.literal_eval — a JSON-stringified list of ints
      // parses cleanly as a Python literal.
      itemIds: JSON.stringify(payload.itemIds),
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
      forceRefresh: payload.forceRefresh ? true : undefined,
    },
  );
  return {
    key: payload.cacheKey,
    data: res.data?.data as ProbeCompareData,
  };
});

export const fetchMaxDiffProbeItemThemes = createAsyncThunk<
  { key: string; data: ProbeItemThemesData },
  {
    batchId: number | string;
    questionId: number | string;
    // Same cache-key pattern as fetchMaxDiffProbeCompare — passed in
    // so the pending/rejected reducers can use it too.
    cacheKey: string;
    languageId?: string;
    filterUserIds?: number[] | null;
    // Bypasses the server-side persistent cache. Wired to the theme
    // strip's "Regenerate" button.
    forceRefresh?: boolean;
  }
>('maxdiffAnalysis/fetchProbeItemThemes', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/probe_item_themes/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
      forceRefresh: payload.forceRefresh ? true : undefined,
    },
  );
  return {
    key: payload.cacheKey,
    data: res.data?.data as ProbeItemThemesData,
  };
});

export const fetchMaxDiffTurf = createAsyncThunk<
  TurfData,
  {
    batchId: number | string;
    questionId: number | string;
    languageId?: string;
    filterUserIds?: number[] | null;
    itemsPool?: number[] | null;   // null / undefined = all items
    comboSize?: number;
    topN?: number;
    appealMode?: 'best' | 'positive_net';
  }
>('maxdiffAnalysis/fetchTurf', async (payload) => {
  const res = await apiServiceInstanceDashboard.post(
    '/analysis/maxdiff/turf/',
    {
      batchId: payload.batchId,
      questionId: payload.questionId,
      languageId: payload.languageId || 'en',
      filterUserIds: payload.filterUserIds
        ? JSON.stringify(payload.filterUserIds)
        : undefined,
      itemsPool: payload.itemsPool && payload.itemsPool.length > 0
        ? JSON.stringify(payload.itemsPool)
        : undefined,
      comboSize: payload.comboSize,
      topN: payload.topN,
      appealMode: payload.appealMode,
    },
  );
  return res.data?.data as TurfData;
});
